import {
  AloftArea,
  AloftCount,
  AloftDate,
  AloftPrice,
  schAloftArea,
  schAloftCount,
  schAloftDate,
  schAloftPrice,
  schFormNumberStringOnly,
  schGeoListingsFilters,
  schGeoSearch,
  schSavedPairCriteria,
  schStudiesSearch,
  schToolkitProperty_,
} from '@aloft/toolkit-shared';

import { z } from 'zod';

import { CONDITION_OPTIONS, QUALITY_OPTIONS } from './constants.js';

const schUndefinedPassthrough = z.literal('').pipe(z.undefined());

export const schPrice = z
  .string()
  .default('')
  .pipe(z.coerce.number().min(0))
  .transform((val) => AloftPrice.fromDollarLike(val))
  .or(schUndefinedPassthrough);

const schDate = z
  .string()
  .default('')
  .transform((val, ctx) => {
    try {
      return AloftDate.fromIsoString(val);
    } catch (err) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid ISO format',
        path: ctx.path,
      });
      return z.NEVER;
    }
  })
  .or(schUndefinedPassthrough);

const schArea = z
  .string()
  .default('')
  .pipe(z.coerce.number().min(0))
  .transform((val) => AloftArea.fromNumber(val, 'sqft'))
  .or(schUndefinedPassthrough);

const schCount = z
  .string()
  .default('')
  .pipe(z.coerce.number().min(0))
  .transform((val) => AloftCount.fromNumber(val))
  .or(schUndefinedPassthrough);

const schProperty = z
  .object({
    salePrice: schPrice,
    financialConcessionAmount: schPrice,
    closeDate: schDate,
    site: schArea,
    locationFactorOne: z.string().optional(),
    viewFactorOne: z.string().optional(),
    designStyle: z.string().optional(),
    quality: z.string().optional(),
    age: z.string().pipe(z.coerce.number().min(0)).or(z.undefined()),
    condition: z.string().optional(),
    grossLivingArea: schArea,
    aboveGradeBedroomCount: schCount,
    aboveGradeFullBathCount: schCount,
    aboveGradeHalfBathCount: schCount,
    totalBasementArea: schArea,
    finishedBasementArea: schArea,
    attachedGarageParkingSpacesCount: schCount,
    carportParkingSpacesCount: schCount,
    porchPatioDeck: z.string().optional(),
    cooling: z.string().optional(),
    greenEnergyEfficient: z.string().optional(),
    heating: z.string().optional(),
    poolFeatures: z.string().optional(),
    custom: z.string().optional(),
    additionalFeature1Description: z.string().optional(),
    additionalFeature2Description: z.string().optional(),
    additionalFeature3Description: z.string().optional(),
  })
  .passthrough();

const schAdjustment = schFormNumberStringOnly
  .transform((val) => AloftPrice.fromDollarLike(val))
  .or(schUndefinedPassthrough);

const schAdjustments = z.object({
  financialConcessionsManual: schAdjustment,
  dateOfSaleManual: schAdjustment,
  siteManual: schAdjustment,
  locationManual: schAdjustment,
  viewManual: schAdjustment,
  designManual: schAdjustment,
  qualityManual: schAdjustment,
  ageManual: schAdjustment,
  conditionManual: schAdjustment,
  grossLivingAreaManual: schAdjustment,
  aboveGradeBedroomCount: schAdjustment,
  aboveGradeFullBathCount: schAdjustment,
  aboveGradeHalfBathCount: schAdjustment,
  totalBasementAreaManual: schAdjustment,
  finishedBasementAreaManual: schAdjustment,
  garageParkingSpacesCount: schAdjustment,
  carportParkingSpacesCount: schAdjustment,
  porchPatioDeckManual: schAdjustment,
  cooling: schAdjustment,
  greenEnergyEfficient: schAdjustment,
  heating: schAdjustment,
  pool: schAdjustment,
  custom: schAdjustment,
  additionalFeature1: schAdjustment,
  additionalFeature2: schAdjustment,
  additionalFeature3: schAdjustment,
});

export const schPair = z.object({
  p1: schProperty,
  p2: schProperty,
  adjustments: schAdjustments,
  additionalFeature1Title: z.string().optional(),
  additionalFeature2Title: z.string().optional(),
  additionalFeature3Title: z.string().optional(),
});

export const schPairedSalesForm = z.object({
  selectedPairs: z.array(schPair),
  reconciledValue: schFormNumberStringOnly
    .pipe(z.coerce.number())
    .transform((val) => AloftPrice.fromDollarLike(val)),
  notes: z.string().optional(),
  search: schStudiesSearch,
});

const schNewPrice = schAloftPrice
  .optional()
  .transform((val) => val?.toDollarLike().toString() ?? '');
const schNewDate = schAloftDate.optional().transform((val) => val?.toIsoString() ?? '');
const schNewArea = schAloftArea.optional().transform((val) => val?.unitAmount.toString() ?? '');
const schNewCount = schAloftCount.optional().transform((val) => val?.toString() ?? '');

const newFormProperty = schToolkitProperty_.merge(
  z.object({
    salePrice: schNewPrice,
    financialConcessionAmount: schNewPrice,
    closeDate: schNewDate,
    site: schNewArea,
    locationFactorOne: z.string().default(''),
    viewFactorOne: z.string().default(''),
    designStyle: z.string().default(''),
    quality: z.enum(QUALITY_OPTIONS).default(''),
    age: z.number().pipe(z.coerce.string()),
    condition: z.enum(CONDITION_OPTIONS).default(''),
    grossLivingArea: schNewArea,
    aboveGradeBedroomCount: schNewCount,
    aboveGradeFullBathCount: schNewCount,
    aboveGradeHalfBathCount: schNewCount,
    totalBasementArea: schNewArea,
    finishedBasementArea: schNewArea,
    attachedGarageParkingSpacesCount: schNewCount,
    carportParkingSpacesCount: schNewCount,
    porchPatioDeck: z.string().default(''),
    cooling: z.string().default(''),
    greenEnergyEfficient: z.string().default(''),
    heating: z.string().default(''),
    poolFeatures: z.string().default(''),
    custom: z.string().default(''),
  }),
);

const schNewAdjustment = schAloftPrice
  .optional()
  .transform((val) => val?.toDollarLike().toString() ?? '');

export const schNewFormPair = z.object({
  p1: newFormProperty,
  p2: newFormProperty,
  adjustments: z.object({
    financialConcessionsManual: schNewAdjustment,
    dateOfSaleManual: schNewAdjustment,
    siteManual: schNewAdjustment,
    locationManual: schNewAdjustment,
    viewManual: schNewAdjustment,
    designManual: schNewAdjustment,
    qualityManual: schNewAdjustment,
    ageManual: schNewAdjustment,
    conditionManual: schNewAdjustment,
    grossLivingAreaManual: schNewAdjustment,
    aboveGradeBedroomCount: schNewAdjustment,
    aboveGradeFullBathCount: schNewAdjustment,
    aboveGradeHalfBathCount: schNewAdjustment,
    totalBasementAreaManual: schNewAdjustment,
    finishedBasementAreaManual: schNewAdjustment,
    garageParkingSpacesCount: schNewAdjustment,
    carportParkingSpacesCount: schNewAdjustment,
    porchPatioDeckManual: schNewAdjustment,
    cooling: schNewAdjustment,
    greenEnergyEfficient: schNewAdjustment,
    heating: schNewAdjustment,
    pool: schNewAdjustment,
    custom: schNewAdjustment,
    additionalFeature1: schNewAdjustment,
    additionalFeature2: schNewAdjustment,
    additionalFeature3: schNewAdjustment,
  }),
  additionalFeature1Title: z.string().optional(),
  additionalFeature2Title: z.string().optional(),
  additionalFeature3Title: z.string().optional(),
});

export const schNewForm = z.object({
  selectedPairs: z.array(schNewFormPair),
  reconciledValue: schAloftPrice
    .transform((v) => v.toDollarLike().toString())
    .or(z.coerce.string()),
  notes: z.string().optional(),
  search: z.object({
    // copied from schStudiesSearch but with an optional `geoSearch`
    filters: schGeoListingsFilters,
    pairCriteria: schSavedPairCriteria,
    geoSearch: schGeoSearch.optional(),
  }),
});
